import { Link } from "react-router-dom";
import MailIcon from "../../../img/svg/mailIcon";
import PhoneIcon from "../../../img/svg/phoneIcon";
import WatchIcon from "../../../img/svg/watchIcon";
import VerifiedWatch from "../../../img/svg/verifiedWatch";
import CheckIcon from "../../../img/svg/checkIcon";
import { BUYER_SCORE_WEIGHTS } from "../../../utils/buyerScore";
import StarIcon from "../../../img/svg/starIcon";

interface IStep {
    title: string;
    description: string;
    items: string[];
    icon: JSX.Element;
    reverse?: boolean;
}

const steps: IStep[] = [
    {
        title: "E-mail verificado",
        description: `Confirme seu e-mail em "/me" para somar ${BUYER_SCORE_WEIGHTS.emailVerified} pontos ao seu score.`,
        items: ["Ajuda o anunciante a confirmar sua identidade", "Leva menos de um minuto"],
        icon: <MailIcon width="80px" fill="#111827" />,
    },
    {
        title: "Telefone verificado",
        description: `Verifique seu telefone por SMS para somar ${BUYER_SCORE_WEIGHTS.phoneVerified} pontos — o maior peso individual do score.`,
        items: ["Sinal forte de que você é uma pessoa real", "Facilita o contato direto com o anunciante"],
        icon: <PhoneIcon width="80px" fill="#111827" />,
        reverse: true,
    },
    {
        title: "Tempo de conta",
        description: `Quanto mais tempo sua conta existe na plataforma, mais pontos você acumula, até ${BUYER_SCORE_WEIGHTS.accountAge} pontos ao completar um ano.`,
        items: ["Cresce automaticamente com o tempo", "Sem nenhuma ação necessária"],
        icon: <WatchIcon width="80px" fill="#111827" />,
    },
    {
        title: "Compras concluídas",
        description: `Cada relógio comprado através da plataforma aumenta seu score, até ${BUYER_SCORE_WEIGHTS.purchases} pontos — o critério de maior peso.`,
        items: ["O sinal mais forte de experiência real", "Poucas compras já fazem grande diferença"],
        icon: <VerifiedWatch width="80px" />,
        reverse: true,
    },
    {
        title: "Avaliações feitas",
        description: `Avalie os anunciantes após suas compras para somar até ${BUYER_SCORE_WEIGHTS.reviews} pontos.`,
        items: ["Ajuda outros compradores a decidir", "Fortalece a comunidade da plataforma"],
        icon: <StarIcon width="70px" fill="#111827" />,
    },
];

export default function BuyerScoreGuide() {
    return (
        <main className="bg-white pb-32 pt-24 overflow-hidden">
            <div className="max-w-4xl mx-auto px-6 text-center mb-20 animate-superfade">
                <p className="text-secondary font-bold tracking-[0.4em] text-xs uppercase mb-6">Negocie com Confiança</p>
                <h1 className="font-outfit text-4xl md:text-6xl font-black text-primary tracking-widest mb-8">
                    SCORE DO <br className="hidden md:block" /> COMPRADOR
                </h1>
                <div className="flex justify-center mb-10">
                    <div className="w-20 h-1 bg-secondary rounded-full" />
                </div>
                <p className="text-xl text-mono leading-relaxed max-w-2xl mx-auto">
                    Seu score é calculado automaticamente a partir da sua atividade na plataforma. Ele ajuda o anunciante a entender, de forma rápida, o nível de experiência de quem está do outro lado da negociação — e é exibido a você e a ele em conjunto com um selo (Bronze, Prata, Ouro ou Diamante).
                </p>
            </div>

            <div className="max-w-4xl mx-auto px-6 space-y-32">
                {steps.map((step) => (
                    <div key={step.title} className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center animate-superfade">
                        <div className={`space-y-6 ${step.reverse ? "order-1 md:order-2" : ""}`}>
                            <div className="w-12 h-1 bg-secondary" />
                            <h2 className="text-2xl font-black text-primary uppercase tracking-widest">{step.title}</h2>
                            <p className="text-mono leading-relaxed">{step.description}</p>
                            <ul className="space-y-3 pt-2">
                                {step.items.map((item) => (
                                    <li key={item} className="flex items-center gap-3">
                                        <CheckIcon width="16px" fill="#C5A059" />
                                        <span className="text-primary font-medium">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className={`hidden bg-moninho aspect-square rounded-[3rem] p-12 md:flex items-center justify-center shadow-xl shadow-primary/5 ${step.reverse ? "order-2 md:order-1" : ""}`}>
                            {step.icon}
                        </div>
                    </div>
                ))}

                <section className="bg-primary rounded-[3rem] p-12 md:p-20 text-white relative overflow-hidden shadow-2xl text-center">
                    <div className="relative z-10 max-w-2xl mx-auto space-y-8">
                        <h3 className="text-secondary font-bold tracking-widest text-sm uppercase">Seu Score, Sua Reputação</h3>
                        <p className="text-2xl md:text-3xl font-black leading-tight">
                            Cada verificação e cada compra <span className="text-secondary">fortalecem sua reputação</span> na plataforma. Acesse sua área para ver seu score atual.
                        </p>
                        <Link
                            to="/me"
                            className="inline-block py-5 px-12 bg-secondary text-white font-black tracking-[0.2em] text-xs rounded-2xl hover:bg-secondary/90 transition-all shadow-xl shadow-black/20 hover:scale-105 active:scale-95"
                        >
                            VER MEU SCORE
                        </Link>
                    </div>

                    <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
                </section>
            </div>

            <div className="fixed top-0 right-0 w-[500px] h-[500px] bg-secondary/[0.02] rounded-full blur-[120px] -z-10 pointer-events-none" />
            <div className="fixed bottom-0 left-0 w-[300px] h-[300px] bg-primary/[0.02] rounded-full blur-[100px] -z-10 pointer-events-none" />
        </main>
    )
}
